import { getStroke } from 'perfect-freehand';

export type DrawingTool = 'pen' | 'eraser';

export type DrawingPoint = {
  x: number;
  y: number;
  pressure?: number;
  timestamp: number;
};

export type DrawingStroke = {
  tool: DrawingTool;
  points: DrawingPoint[];
  color: string;
  size: number;
};

export type DrawingData = {
  version: 1;
  viewBox: {
    width: number;
    height: number;
  };
  strokes: DrawingStroke[];
  backgroundDataUrl?: string;
};

const loadImageElement = (dataUrl: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Failed to load background image'));
    image.src = dataUrl;
  });

const buildStrokeOutline = (stroke: DrawingStroke) => {
  const points = stroke.points.map((point) => [point.x, point.y, point.pressure ?? 0.5]);
  return getStroke(points, {
    size: stroke.size,
    thinning: 0.5,
    smoothing: 0.5,
    streamline: 0.5,
    simulatePressure: stroke.points.every((point) => point.pressure == null),
  });
};

export const getSvgPathFromStroke = (stroke: number[][]) => {
  if (!stroke.length) return '';
  const d = stroke.reduce<string[]>((acc, [x0, y0], index, points) => {
    const [x1, y1] = points[(index + 1) % points.length];
    acc.push(x0.toString(), y0.toString(), ((x0 + x1) / 2).toString(), ((y0 + y1) / 2).toString());
    return acc;
  }, ['M', stroke[0][0].toString(), stroke[0][1].toString(), 'Q']);
  d.push('Z');
  return d.join(' ');
};

export const exportAsSVG = (
  strokes: DrawingStroke[],
  width: number,
  height: number,
  backgroundDataUrl?: string
) => {
  const background = backgroundDataUrl
    ? `<image href="${backgroundDataUrl}" x="0" y="0" width="${width}" height="${height}" />`
    : '';
  const paths = strokes
    .map((stroke, index) => {
      const outline = buildStrokeOutline(stroke);
      const path = getSvgPathFromStroke(outline);
      if (!path) return '';
      const fill = stroke.tool === 'eraser' ? '#ffffff' : stroke.color;
      return `<path d="${path}" fill="${fill}" data-tool="${stroke.tool}" data-index="${index}" />`;
    })
    .join('');

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">${background}${paths}</svg>`;
};

export const exportAsPNGWithTransparency = async (
  strokes: DrawingStroke[],
  width: number,
  height: number,
  backgroundDataUrl?: string
) => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return Promise.reject(new Error('Canvas context not available'));
  }

  ctx.clearRect(0, 0, width, height);

  if (backgroundDataUrl) {
    const image = await loadImageElement(backgroundDataUrl);
    ctx.globalCompositeOperation = 'source-over';
    ctx.drawImage(image, 0, 0, width, height);
  }

  strokes.forEach((stroke) => {
    const outline = buildStrokeOutline(stroke);
    const pathData = getSvgPathFromStroke(outline);
    if (!pathData) return;
    const path = new Path2D(pathData);
    ctx.globalCompositeOperation = stroke.tool === 'eraser' ? 'destination-out' : 'source-over';
    ctx.fillStyle = stroke.color;
    ctx.fill(path);
  });

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('Failed to export PNG'));
      }
    }, 'image/png');
  });
};

export const exportAsAnimationData = (
  strokes: DrawingStroke[],
  width: number,
  height: number,
  backgroundDataUrl?: string
): DrawingData => ({
  version: 1,
  viewBox: { width, height },
  strokes,
  backgroundDataUrl,
});
